import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Editing, Selection } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { FormRef, GroupItem, Label, SimpleItem } from "devextreme-react/form";
import IActivite from "../../models/IActivite";
import IGestionActivite from "../../models/IGestionActivite";
import { FormListBase, FormListBaseGrid, FormListBaseTab, FormListBaseToolbar } from '../base';
import { CheckBox } from 'devextreme-react';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useLookUpMois from '../../hooks/useLookupMois';
import useLookUpBase from '../../components/selectbox/LookupBase';
import Guid from "../../models/Guid";
import GestionCatalogueProduits from '../gestion-catalogue-produits/gestion-catalogue-produits';
import useApi from '../../hooks/useApi';
import IGibier from '../../models/IGibier';
import useEnum from '../../hooks/useEnum';
import IActiviteGibier from '../../models/IActiviteGibier';
import { useContextPopupPage } from '../../components/popups/PopupPage';
import GestionActiviteSecteurs from './gestion-activite-secteurs';
import GestionActivitePointsEau from './gestion-activite-points-eau';

interface ActiviteGibiers {
  idActiviteGibier?: Guid;
  idActivite: string;
  idGibier: string;
  isDeleted: boolean;
}

interface IGestionActiviteProps {
  isModeSelection?: boolean,
}

export default function GestionActivites({ isModeSelection }: IGestionActiviteProps) {
  const activiteDataSourceOption: IFormBaseDataSourceOptions<IGestionActivite> = useMemo(() => ({
    path: "activite",
    customReadByIdPath: "activite/{key}/gestion",
    customSauvegarderPath: "activite/save",
    primaryKey: "idActivite",
  }), []);
  const activiteFormDataSource = useCustomDataSource<IActivite, IGestionActivite>(activiteDataSourceOption);
  // const journalFormDataSource = useCustomDataSource<IJournal, IGestionJournal>(activiteDataSourceOption);

  const [dataFromTypeGibier, setDataFormTypeGibier] = useState<IGibier[]>([])
  const { setMainForm, selectedDataRow: selectedActivite, mode, onFieldDataChanged, setMode, isFieldModifier, setIsFieldModifier, setSelectedDataRow } = activiteFormDataSource;
  const { setSelectedValue } = useContextPopupPage();
  const [dataActiviteGibier, setDataActiviteGibier] = useState<ActiviteGibiers[]>([]);
  const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);
  const formRef = useRef<FormRef>(null);
  const { MoisSelectBox } = useLookUpMois()
  const { fetchData, getData } = useApi('gibier');
  const { editorOptions: typesActiviteEditorOptions } = useEnum(undefined, "gestionTypesActivite");
  const { editorOptions: editorOptionsProduitAnnuel1, PopupGestion: PopupGestionProduitAnnuel1 } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "catalogue", showClear: mode !== Mode.Lecture, titre: "Catalogue de produits",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idProduitAnnuel1", Guid.empty.toString());
    }
  });
  const { editorOptions: editorOptionsProduitAnnuel2, PopupGestion: PopupGestionProduitAnnuel2 } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "catalogue", showClear: mode !== Mode.Lecture, titre: "Catalogue de produits",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idProduitAnnuel2", Guid.empty.toString());
    }
  });
  const { editorOptions: editorOptionsProduitJournalier, PopupGestion: PopupGestionProduitJournalier } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "catalogue", showClear: mode !== Mode.Lecture, titre: "Catalogue de produits",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idProduitJournalier", Guid.empty.toString());
    }
  });

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])

  useEffect(() => {
    getData()
      .then((result) => {
        if (result) {
          const formData = result as IGibier[];
          setDataFormTypeGibier(formData);
        }
      })
      .catch((err) => {
        console.log('Erreur lors de la récupération des données :', err);
      });

    fetchData('get', { customPath: `ActiviteGibiers/${selectedActivite.idActivite}/list` })
      .then((result) => {
        if (result) {
          setDataActiviteGibier(result)
        }
      })
      .catch((err) => {
        console.log('Erreur lors de la récupération des données :', err);
      });

  }, [])

  useEffect(() => {
    if (selectedActivite?.activiteGibiers && dataFromTypeGibier.length > 0) {
      const initialData: ActiviteGibiers[] = selectedActivite.activiteGibiers
        .filter(item => !item.isDeleted)
        .map(item => ({
          idActivite: selectedActivite.idActivite?.toString() || '',
          idGibier: item.idGibier?.toString() || '',
          isDeleted: false
        }));
      setDataActiviteGibier(initialData);
    }
  }, [selectedActivite?.activiteGibiers, dataFromTypeGibier])

  const onSelectionchanged = (item: any, e: string) => {
    setIsFieldModifier(true);
    setSelectedDataRow((prev: IGestionActivite) => {
      return { ...prev, [e]: item.id }
    })
  }

  const { descriptionOptions, chasseOptions, pecheOptions, travailleurOptions, villegiatureOptions, vttOptions, autreOptions, actifOptions, demanderPointEauOptions, demanderSecteurOptions, validerPointEauOptions, validerSecteurOptions, disponibleEnLigneLocal, permettreDesenregistrementOptions } = useMemo(() => ({
    descriptionOptions: { maxLength: 50 },
    chasseOptions: { text: "Chasse" },
    pecheOptions: { text: "Pêche" },
    travailleurOptions: { text: "Travailleur" },
    villegiatureOptions: { text: "Villégiature" },
    vttOptions: { text: "VTT" },
    autreOptions: { text: "Autre" },
    actifOptions: { text: "Actif" },
    demanderPointEauOptions: { text: "Demander le point d'eau" },
    demanderSecteurOptions: { text: "Demander le secteur" },
    validerPointEauOptions: { text: "Valider le point d'eau" },
    validerSecteurOptions: { text: "Valider le secteur" },
    disponibleEnLigneLocal: { text: "Disponible en ligne" },
    permettreDesenregistrementOptions: { text: "Permettre de désenregistrer des clients" },
  }), []);


  const RenderSimple = (e: any) => {
    const detailsSet = new Set<string>(dataActiviteGibier.map(detail => detail?.idGibier))

    const onValueChanged = useCallback((value: boolean, gibierId: string) => {

      if (gibierId && selectedActivite.idActivite) {
        if (value) {
          const newItem: ActiviteGibiers = {
            idActivite: selectedActivite?.idActivite?.toString() || '',
            idGibier: gibierId,
            isDeleted: false
          };
          setDataActiviteGibier(prev => [...prev, newItem]);

          const index = selectedActivite.activiteGibiers.findIndex(item =>
            item.idGibier.toString() == gibierId
          );
          if (index !== -1) {
            const updatedData = [...selectedActivite.activiteGibiers];
            updatedData[index] = { ...updatedData[index], isDeleted: false };
            setSelectedDataRow(prev => ({
              ...prev,
              activiteGibiers: updatedData
            }));
          } else {
            setSelectedDataRow(prev => ({
              ...prev,
              activiteGibiers: [...prev.activiteGibiers, newItem] as IActiviteGibier[]
            }));
          }
        } else {
          setDataActiviteGibier(prev => prev.filter(item => item.idGibier !== gibierId));
          const index = dataActiviteGibier.findIndex(item =>
            item.idGibier == gibierId
          );
          if (index !== -1) {
            const updatedData = [...selectedActivite.activiteGibiers];
            updatedData[index] = { ...updatedData[index], isDeleted: true };
            setSelectedDataRow(prev => ({
              ...prev,
              activiteGibiers: updatedData
            }));
          }
        }
        setIsFieldModifier(true);
      }
    }, [selectedActivite?.idActivite]);

    return (
      <div>
        {dataFromTypeGibier?.map((item, index) => (
          <div key={index}>
            <CheckBox
              value={detailsSet.has(item.idGibier
                ? item.idGibier.toString()
                : '')}
              disabled={mode === Mode.Lecture}
              onValueChanged={(e) => onValueChanged(e.value, item.idGibier?.toString() || '')}
            />
            {item.nom}
          </div>
        ))}
      </div>
    );
  };

  return (
    <FormListBase data={activiteFormDataSource}>
      <FormListBaseGrid dataSource={activiteFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture);
          }
          if (setSelectedValue && e.row?.data) {
            setSelectedValue(e.row.data);
          }
        }}
      >
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-peche"} dataField={"peche"} caption="Pêche" allowHiding={false} />
        <Column key={"grid-col-chasse"} dataField={"chasse"} caption="Chasse" allowHiding={false} />
        <Column key={"grid-col-vtt"} dataField={"vtt"} caption="VTT" allowHiding={false} />
        <Column key={"grid-col-travailleur"} dataField={"travailleur"} caption="Travailleur" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedActivite?.description || ''}`} effacerOptions={{ disabled: !isFieldModifier }} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture
        ? <FormListBaseTab>
          {/* {PopupGestionProduitAnnuel1 &&
            <PopupGestionProduitAnnuel1
              onSelectionchanged={(data: any) => {
                setIsFieldModifier(true);
                formRef.current?.instance().updateData("idProduitAnnuel1", data.idProduitAnnuel1);
              }}
            >
              <GestionCatalogueProduits isModeSelection={false} />
            </PopupGestionProduitAnnuel1>}
          {PopupGestionProduitAnnuel2 &&
            <PopupGestionProduitAnnuel2
              onSelectionchanged={(data: any) => {
                setIsFieldModifier(true);
                formRef.current?.instance().updateData("idProduitAnnuel2", data.idProduitAnnuel1);
              }}
            >
              <GestionCatalogueProduits isModeSelection={true} />
            </PopupGestionProduitAnnuel2>}
          {PopupGestionProduitJournalier &&
            <PopupGestionProduitJournalier
              onSelectionchanged={(data: any) => {
                setIsFieldModifier(true);
                formRef.current?.instance().updateData("idProduitJournalier", data.idProduitAnnuel1);
              }}
            >
              <GestionCatalogueProduits isModeSelection={true} />
            </PopupGestionProduitJournalier>} */}

          <PopupGestionProduitAnnuel1
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idProduitAnnuel1", data.idProduit);
            }}
          >
            <GestionCatalogueProduits isModeSelection={false} />
          </PopupGestionProduitAnnuel1>
          <PopupGestionProduitAnnuel2
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idProduitAnnuel2", data.idProduit);
            }}
          >
            <GestionCatalogueProduits isModeSelection={true} />
          </PopupGestionProduitAnnuel2>
          <PopupGestionProduitJournalier
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idProduitJournalier", data.idProduit);
            }}
          >
            <GestionCatalogueProduits isModeSelection={true} />
          </PopupGestionProduitJournalier>

          <TabItem title="Informations">
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedActivite || {} as IActivite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <GroupItem colCount={4}>
                <GroupItem colSpan={2}>
                  <SimpleItem key={"frm-description"} dataField={"description"} editorOptions={descriptionOptions} />
                  <SimpleItem key={"frm-mois-debut"} dataField={"moisDebut"} editorType={"dxSelectBox"} render={MoisSelectBox}>
                    <Label text={"Mois début"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-mois-fin"} dataField={"moisFin"} editorType={"dxSelectBox"} render={MoisSelectBox}>
                    <Label text={"Mois fin"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-id-produit-annuel1"} dataField={"idProduitAnnuel1"} editorType='dxSelectBox' editorOptions={editorOptionsProduitAnnuel1}>
                    <Label text={"Produit annuel 1"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-id-produit-annuel2"} dataField={"idProduitAnnuel2"} editorType='dxSelectBox' editorOptions={editorOptionsProduitAnnuel2}>
                    <Label text={"Produit annuel 2"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-id-produit-journalier"} dataField={"idProduitJournalier"} editorType='dxSelectBox' editorOptions={editorOptionsProduitJournalier}>
                    <Label text={"Produit journalier à facturer"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-pas-facturer-circulation"} dataField={"pasFacturerCirculation"} editorType='dxCheckBox'>
                    <Label text={"Ne pas facturer la circulation"} />
                  </SimpleItem>
                </GroupItem>
                <GroupItem caption={"Type d'activité"} colSpan={1}>
                  <SimpleItem key={"frm-chasse"} dataField={"chasse"} editorType={"dxCheckBox"} editorOptions={chasseOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-peche"} dataField={"peche"} editorType={"dxCheckBox"} editorOptions={pecheOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-travailleur"} dataField={"travailleur"} editorType={"dxCheckBox"} editorOptions={travailleurOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-villegiature"} dataField={"villegiature"} editorType={"dxCheckBox"} editorOptions={villegiatureOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-vtt"} dataField={"vtt"} editorType={"dxCheckBox"} editorOptions={vttOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-autre"} dataField={"autre"} editorType={"dxCheckBox"} editorOptions={autreOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
                <GroupItem caption={"Options"} colSpan={1}>
                  <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"} editorOptions={actifOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-demander-point-eau"} dataField={"demanderPointEau"} editorType={"dxCheckBox"}
                    editorOptions={demanderPointEauOptions}
                  >
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-demander-secteur"} dataField={"demanderSecteur"} editorType={"dxCheckBox"}
                    editorOptions={demanderSecteurOptions}
                  >
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-valider-point-eau"} dataField={"validerPointEau"} editorType={"dxCheckBox"}
                    editorOptions={validerPointEauOptions}
                  >
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-valider-secteur"} dataField={"validerSecteur"} editorType={"dxCheckBox"}
                    editorOptions={validerSecteurOptions}
                  >
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
              {selectedActivite?.chasse &&
                <GroupItem caption='Sélection des gibiers'>
                  <SimpleItem render={() => <RenderSimple />} />
                </GroupItem>
              }
            </Form>
          </TabItem>
          <TabItem title="Secteurs">
            <GestionActiviteSecteurs
              mode={mode}
              selectedActivite={selectedActivite}
              setSelectedDataRow={setSelectedDataRow}
              setIsFieldModifier={setIsFieldModifier}
              onFieldDataChanged={onFieldDataChanged}
            />
          </TabItem>
          <TabItem title="Points d'eau">
            <GestionActivitePointsEau
              mode={mode}
              selectedActivite={selectedActivite}
              setSelectedDataRow={setSelectedDataRow}
              setIsFieldModifier={setIsFieldModifier}
              onFieldDataChanged={onFieldDataChanged}
            />
          </TabItem>
          <TabItem title="Internet">
            <Form readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedActivite || {} as IActivite} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <SimpleItem key={"frm-disponible-en-ligne"} dataField={"disponibleEnLigne"} editorType={"dxCheckBox"}
                editorOptions={disponibleEnLigneLocal}
              >
                <Label visible={false} />
              </SimpleItem>
              <SimpleItem key={"frm-permettre-desenregistrement"} dataField={"permettreDesenregistrement"} editorType={"dxCheckBox"}
                editorOptions={
                  permettreDesenregistrementOptions
                }
              >
                <Label visible={false} />
              </SimpleItem>
              <GroupItem colCount={2}>
                <SimpleItem key={"frm-type-activite"} dataField={"typeActivite"} editorType={"dxSelectBox"}
                  editorOptions={
                    typesActiviteEditorOptions
                  }
                >
                  <Label text={"Type d'activité"} />
                </SimpleItem>
              </GroupItem>
              <GroupItem colCount={2} caption={"Dates d'indisponibilité sur le PAV"}>
                <GroupItem colSpan={1}>
                  <DataGrid
                    showBorders={true}
                    dataSource={selectedActivite.activiteHorsServices}
                    allowColumnReordering={false}
                    // onRowInserted={onCellClickPermis}
                    filterValue={dataGridFilterValue}
                    height={"100%"}
                    // focusedRowEnabled={true}
                    // defaultFocusedRowIndex={0}
                    // autoNavigateToFocusedRow={true}
                    noDataText={"Aucune date d'indisponibilité"}
                    onInitNewRow={(e: any) => {
                      e.data.idActivite = selectedActivite.idActivite;
                      e.data.isDeleted = false;
                    }}
                    // onRowInserting={(e: any) => {
                    //   if (!e.data.dateDebut || !e.data.dateFin) {
                    //     e.cancel = true;
                    //   }
                    // }}
                    onEditorPreparing={(e: any) => {
                      // if (e.dataField === "idPermisType") {
                      const originalOnValueChanged = e.editorOptions.onValueChanged;
                      e.editorOptions.onValueChanged = (args: any) => {
                        if (originalOnValueChanged) {
                          originalOnValueChanged(args);
                        }

                        const currentData = e.component.option("dataSource");
                        const rowIndex = e.rowIndex;
                        const rowData = currentData[rowIndex];

                        if (!rowData.idActiviteHorsService) {
                          const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                          setSelectedDataRow(prev => ({
                            ...prev,
                            activiteHorsServices: updatedData
                          }));
                          setIsFieldModifier(true);
                        }
                      };
                      // }
                    }}
                    onRowRemoving={async (e) => {
                      e.cancel = true;
                      // if (e?.data?.idPermisType) {
                      const index = selectedActivite.activiteHorsServices.findIndex(item =>
                        item.idActiviteHorsService?.equals(e.data.idActiviteHorsService)
                      );
                      if (index !== -1) {
                        const updatedData = [...selectedActivite.activiteHorsServices];
                        updatedData[index] = { ...updatedData[index], isDeleted: true };
                        setSelectedDataRow(prev => ({
                          ...prev,
                          activiteHorsServices: updatedData
                        }));
                        setIsFieldModifier(true);
                      }
                      // }
                    }}
                  >
                    <Editing
                      mode="cell"
                      allowUpdating={mode !== Mode.Lecture}
                      allowDeleting={mode !== Mode.Lecture}
                      allowAdding={mode !== Mode.Lecture}
                      newRowPosition={"first"}
                      startEditAction={"click"}
                    />
                    <Column dataField="dateDebut" dataType="date" caption={"Date de début"} format="dd-MM-yyyy HH:mm" />
                    <Column dataField="dateFin" dataType="date" caption={"Date de fin"} format="dd-MM-yyyy HH:mm" />
                    <Column dataField="isDeleted" dataType="boolean" visible={false} />
                  </DataGrid>
                </GroupItem>
              </GroupItem>
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={selectedActivite || {} as IActivite} /></TabItem>
        </FormListBaseTab> : null
      }
    </FormListBase>
  );
}